import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import './WhyChooseUs.css';
import SEOHead from '../SEOHead';
import OptimizedImage from '../OptimizedImage';

const WhyChooseUs: React.FC = () => {
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth'
    });
  }, []);

  const highlights = [
    {
      id: 1,
      icon: (
        <svg width="48" height="48" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
        </svg>
      ),
      title: 'Kashi-Rooted Acharyas',
      description: 'Our Acharyas and Guruvars come from traditional learning lineages, many trained and practicing in Kashi, bringing depth, discipline, and authenticity to every ritual.',
      stats: 'Kashi Acharyas'
    },
    {
      id: 2,
      icon: (
        <svg width="48" height="48" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 2L2 7v10c0 5.55 3.84 9.74 9 11 5.16-1.26 9-5.45 9-11V7l-10-5z"/>
        </svg>
      ),
      title: 'Scriptural Integrity First',
      description: 'Every pooja and anushthan is performed strictly as per shastra, with defined vidhi, mantra discipline, and ritual scope; no shortcuts, no dilution.',
      stats: '100% Authentic Rituals'
    },
    {
      id: 3,
      icon: (
        <svg width="48" height="48" viewBox="0 0 24 24" fill="currentColor">
          <path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67z"/>
        </svg>
      ),
      title: 'End-to-End Responsibility',
      description: 'From pooja samagri and logistics to coordination and scheduling, Antaryatra takes full responsibility, so the focus remains only on the sacred act.',
      stats: '0% Consumer Hassle'
    },
    {
      id: 4,
      icon: (
        <svg width="48" height="48" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
        </svg>
      ),
      title: 'Transparent & Structured Engagement',
      description: 'Clear packages, defined expectations, and respectful execution. No bargaining, no last-minute changes, no confusion at the devotee’s home.',
      stats: '100% Transparent Process'
    },
    {
      id: 5,
      icon: (
        <svg width="48" height="48" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 1L3 5V11C3 16.55 6.84 21.74 12 23C17.16 21.74 21 16.55 21 11V5L12 1M12 7C13.11 7 14 7.89 14 9S13.11 11 12 11 10 10.11 10 9 10.89 7 12 7M18 9C18 13.25 15.39 17 12 17.5C8.61 17 6 13.25 6 9L12 6L18 9Z"/>
        </svg>
      ),
      title: 'Physical & Remote Access',
      description: 'Choose in-person poojas in Delhi NCR or in-person/remote poojas in Varanasi with online sankalp, proper havan, and prasad dispatch.',
      stats: 'Suitable for All Locations'
    },
    {
      id: 6,
      icon: (
        <svg width="48" height="48" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 2C13.1 2 14 2.9 14 4C14 5.1 13.1 6 12 6C10.9 6 10 5.1 10 4C10 2.9 10.9 2 12 2ZM21 9V7L15 1H9V3H15.5L19 6.5V9H21ZM3 13V11L9 5H15V7H9.5L6 10.5V13H3ZM12 8C14.76 8 17 10.24 17 13C17 15.76 14.76 18 12 18C9.24 18 7 15.76 7 13C7 10.24 9.24 8 12 8Z"/>
        </svg>
      ),
      title: 'Dignity-First Collaboration',
      description: 'We work with a limited set of trusted Acharyas, not an open marketplace. Quality, conduct, and mutual respect matter more than volume.',
      stats: 'Quality over Quantity'
    }
  ];

  const testimonials = [
    {
      id: 1,
      name: 'Priya Sharma',
      location: 'Mumbai',
      rating: 5,
      comment: 'The Grih Pravesh ceremony was conducted beautifully. The pandit was very knowledgeable and explained every ritual. Highly recommended!'
    },
    {
      id: 2,
      name: 'Rajesh Kumar',
      location: 'Delhi',
      rating: 5,
      comment: 'Amazing service for my daughter\'s Naam Karan. Everything was perfectly organized and the materials were of premium quality.'
    },
    {
      id: 3,
      name: 'Anjali Patel',
      location: 'Bangalore',
      rating: 5,
      comment: 'Professional service with deep knowledge of traditions. The online booking made it so convenient. Will definitely use again.'
    }
  ];

  const achievements = [
    { number: 'Kashi', label: 'Acharya guidance' },
    { number: '30+', label: 'Structured Pooja & Anusthan Offerings' },
    { number: '100%', label: 'Shastric Compliance Commitment' },
    { number: '2', label: 'Launch Cities (Delhi NCR & Varansi)' }
  ];

  return (
    <div className="why-choose-us-page">
      <SEOHead
        title="Why Choose Antaryatra – Trusted Vedic Puja Platform"
        description="Explore Antaryatra’s approach to Vedic rituals. Verified Kashi pandits, genuine materials, transparent pricing and divine ceremonies."
        canonicalPath="/why-choose-us"
        keywords="why choose Antaryatra, best puja service India, verified pandits, authentic Vedic rituals"
      />
      {/* Hero Section */}
      <section className="why-hero">
        <div className="container">
          <div className="hero-content">
            <h1>Why Choose Our Services?</h1>
            <p>Experience the perfect blend of traditional wisdom and modern convenience for all your spiritual needs</p>
            <Link to="/" className="cta-button">
              Explore Our Services
              <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                <path fillRule="evenodd" d="M1 8a.5.5 0 0 1 .5-.5h11.793l-3.147-3.146a.5.5 0 0 1 .708-.708l4 4a.5.5 0 0 1 0 .708l-4 4a.5.5 0 0 1-.708-.708L13.293 8.5H1.5A.5.5 0 0 1 1 8z"/>
              </svg>
            </Link>
          </div>
          <div className="hero-image">
            <OptimizedImage src="/images/puja_service.jpg" alt="Traditional Puja Service" />
          </div>
        </div>
      </section>

      {/* Achievements Section */}
      <section className="achievements-section">
        <div className="container">
          <div className="achievements-grid">
            {achievements.map((achievement, index) => (
              <div key={index} className="achievement-item">
                <h3>{achievement.number}</h3>
                <p>{achievement.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Highlights Section */}
      <section className="highlights-section">
        <div className="container">
          <div className="section-header">
            <h2>What Makes Us Different</h2>
            <p>Discover the unique advantages that set our services apart from the rest</p>
          </div>
          
          <div className="highlights-grid">
            {highlights.map((highlight) => (
              <div key={highlight.id} className="highlight-card">
                <div className="highlight-icon">
                  {highlight.icon}
                </div>
                <h3>{highlight.title}</h3>
                <p>{highlight.description}</p>
                <div className="highlight-stats">
                  {highlight.stats}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      {/* <section className="testimonials-section">
        <div className="container">
          <div className="section-header">
            <h2>What Our Customers Say</h2>
            <p>Real experiences from families who have trusted us with their sacred ceremonies</p>
          </div>
          
          <div className="testimonials-grid">
            {testimonials.map((testimonial) => (
              <div key={testimonial.id} className="testimonial-card">
                <div className="testimonial-rating">
                  {[...Array(testimonial.rating)].map((_, index) => (
                    <svg key={index} className="star" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                    </svg>
                  ))}
                </div>
                <p>"{testimonial.comment}"</p>
                <div className="testimonial-author">
                  <strong>{testimonial.name}</strong>
                  <span>{testimonial.location}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section> */}

      {/* Process Section */}
      <section className="process-section">
        <div className="container">
          <div className="section-header">
            <h2>Our Simple Process</h2>
            <p>Getting started with our services is easy and straightforward</p>
          </div>
          
          <div className="process-steps">
            <div className="process-step">
              <div className="step-number">1</div>
              <h3>Choose Your Pujan</h3>
              <p>Browse our comprehensive list of traditional ceremonies and select the one you need</p>
            </div>
            
            <div className="process-step">
              <div className="step-number">2</div>
              <h3>Schedule & Customize</h3>
              <p>Pick your preferred date and time, and customize the ceremony according to your requirements</p>
            </div>
            
            <div className="process-step">
              <div className="step-number">3</div>
              <h3>Professional Service</h3>
              <p>Our certified pandits arrive with all materials and conduct the ceremony with devotion</p>
            </div>
            
            <div className="process-step">
              <div className="step-number">4</div>
              <h3>Divine Blessings</h3>
              <p>Receive the spiritual benefits and positive energy from a properly conducted sacred ceremony</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-content">
            <h2>Ready to Plan Your Ceremony?</h2>
            <p>Join thousands of satisfied families who have chosen us for their sacred ceremonies</p>
            <div className="cta-buttons">
              <Link to="/" className="cta-button primary">
                Explore Services
              </Link>
              {/* <a href="tel:1800-123-4567" className="cta-button secondary">
                Call Now: 1800-123-4567
              </a> */}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default WhyChooseUs;